"use client";
/**
 * Imperative dialogs replacing window.alert / confirm / prompt with styled
 * modals. Usage:
 *
 *   if (await confirmDialog("Delete this page?")) { ... }
 *   const name = await promptDialog("New folder name");
 *   await noticeDialog("Could not save the form.");
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

/** UI language for detached dialog roots (outside the React provider tree). */
const uiLocale = () => (typeof document === "undefined" ? "nl" : document.documentElement.lang || "nl");

// Dutch fallbacks so this module carries no localisation payload; the admin
// provider installs the real translator on globalThis while /admin is mounted.
const NL: Record<string, string> = {
  "common.cancel": "Annuleren",
  "common.ok": "OK",
  "common.confirm": "Bevestigen",
  "common.save": "Opslaan",
};
const dt = (key: string) => globalThis.__cmsAdminTranslate?.(uiLocale(), key) ?? NL[key] ?? key;

interface DialogProps {
  title: string;
  kind: "confirm" | "prompt" | "notice";
  initial?: string;
  placeholder?: string;
  danger?: boolean;
  confirmLabel?: string;
  resolve: (value: string | boolean | null) => void;
  done: () => void;
}

function Dialog(props: DialogProps) {
  const [value, setValue] = useState(props.initial || "");
  const inputRef = useRef<HTMLInputElement>(null);

  const close = (result: string | boolean | null) => {
    props.resolve(result);
    props.done();
  };

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(props.kind === "confirm" ? false : null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ok = () => close(props.kind === "prompt" ? value : true);

  return (
    <div
      className="cms-modal-backdrop"
      style={{ zIndex: 6000 }}
      onMouseDown={(e) => e.target === e.currentTarget && close(props.kind === "confirm" ? false : null)}
    >
      <div className="cms-modal" style={{ maxWidth: 420 }} role="dialog" aria-modal="true">
        <div className="cms-modal-body">
          <p style={{ marginTop: 0, whiteSpace: "pre-line" }}>{props.title}</p>
          {props.kind === "prompt" && (
            <input
              ref={inputRef}
              className="cms-input"
              value={value}
              placeholder={props.placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ok()}
            />
          )}
          <div className="cms-modal-actions">
            {props.kind !== "notice" && (
              <button className="cms-btn" onClick={() => close(props.kind === "confirm" ? false : null)}>
                {dt("common.cancel")}
              </button>
            )}
            <button
              className={`cms-btn ${props.danger ? "cms-btn-danger-solid" : "cms-btn-primary"}`}
              onClick={ok}
            >
              {props.confirmLabel || dt("common.ok")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function show(props: Omit<DialogProps, "resolve" | "done">): Promise<string | boolean | null> {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = () => {
      // Unmount on the next tick to avoid React warning about sync unmounts
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
    };
    root.render(<Dialog {...props} resolve={resolve} done={done} />);
  });
}

export async function confirmDialog(title: string, opts: { danger?: boolean; confirmLabel?: string } = {}): Promise<boolean> {
  return (await show({ title, kind: "confirm", danger: opts.danger ?? true, confirmLabel: opts.confirmLabel || dt("common.confirm") })) === true;
}

export async function promptDialog(title: string, initial = "", placeholder = ""): Promise<string | null> {
  const r = await show({ title, kind: "prompt", initial, placeholder, confirmLabel: dt("common.save") });
  return typeof r === "string" ? r : null;
}

export async function noticeDialog(title: string): Promise<void> {
  await show({ title, kind: "notice", danger: false });
}

export function renderNothing(): ReactNode {
  return null;
}
